import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { isEqual } from "lodash";
import RenderExplainLink from "../../components/atoms/RenderExplainLink/RenderExplainLink";
import {
  getTranscripts,
  getGenome,
  getVarsomeHost,
} from "../../state/selectors/general.selectors";
import { setCurrentVariant } from "../../state/actions/setCurrentVariantAction";
import TranscriptDetailsTable from "./TranscriptDetaisTable";
import { getCardsUIEnabled } from "../../state/selectors/CardsUI.selectors";
import HelperText from "../../components/atoms/HelperText";

const TranscriptDetails = (props) => {
  const {
    transcripts,
    genome,
    varsomeHost,
    SetCurrentVariant,
    cardsUIEnabled,
  } = props;
  const [explainVisible, setExplainVisible] = useState(false);
  const [trans, setTranscripts] = useState(transcripts);
  useEffect(() => {
    setTranscripts(transcripts);
  }, [transcripts]);

  const handleExplainClick = (newValue) => {
    setExplainVisible(newValue);
  };

  /* Example transcript to test this functionality: ENST00000387421.1 */
  const zoomToTranscript = (t) => {
    const variantId = String(Date.now());
    const ref = "X".repeat(t.length);

    const variantResult = {
      variant_id: variantId,
      pos: t.position,
      ref,
      alt: "",
      mainTranscript: t,
      chromosome: t.chromo,
    };

    SetCurrentVariant(variantResult);

    /* Unmark all, and mark current as active. */
    const a = trans.map((x) => {
      if (x.active) {
        return {
          ...x,
          active: false,
        };
      }
      if (isEqual(x, t)) {
        return {
          ...x,
          active: true,
        };
      }
      return x;
    });
    setTranscripts(a);
  };

  let tmpl = null;

  if (trans) {
    const t = trans[0];
    const genomeSubdomain = genome.name === "hg38" ? "www" : "grch37";

    tmpl = (
      <div
        className={`varsome-container ${
          cardsUIEnabled ? "varsome-container__basic" : ""
        }`}
      >
        <div className="varsome-title">
          <span className="tw-font-semibold">Transcript details </span>
          <HelperText fieldDescriptionKey="Transcript details" />
          <RenderExplainLink
            setExplainVisible={handleExplainClick}
            label="Explain"
          />
        </div>
        {explainVisible && (
          <div className="varsome-inner-container">
            Gene {t.gene_symbol} consists of one or more splice forms, including{" "}
            {t.name}. The DNA sequence where this transcript originates is on
            the {t.strand === "-" ? "negative" : "positive"} strand of
            chromosome {t.chromo}, position {t.position} and occupies {t.length}{" "}
            base pairs on the chromosome
            {t.mrna_length < t.length && " including introns"}. After
            transcription and splicing, the mRNA transcript is comprised of{" "}
            {t.mrna_length} bases
            {t.coding_length
              ? `, of which ${t.coding_length} are translated into a protein.`
              : ". This is a non-protein-coding transcript."}
          </div>
        )}
        <div className="varsome-inner-container">
          <TranscriptDetailsTable
            varsomeHost={varsomeHost}
            trans={trans}
            handleClick={zoomToTranscript}
            genomeSubdomain={genomeSubdomain}
          />
        </div>
      </div>
    );
  }

  return <div className="varsome-result">{tmpl}</div>;
};

TranscriptDetails.propTypes = {
  transcripts: PropTypes.arrayOf(
    PropTypes.shape({
      active: PropTypes.bool,
      canonical: PropTypes.bool,
      cds_end: PropTypes.number,
      cds_start: PropTypes.number,
      chromo: PropTypes.string,
      coding_length: PropTypes.number,
      coding_transcript: PropTypes.bool,
      cytoband: PropTypes.string,
      exon_count: PropTypes.number,
      exons: PropTypes.arrayOf(
        PropTypes.shape({
          end: PropTypes.number,
          start: PropTypes.number,
        })
      ),
      gene_id: PropTypes.number,
      gene_symbol: PropTypes.string,
      length: PropTypes.number,
      mrna_length: PropTypes.number,
      name: PropTypes.string,
      position: PropTypes.number,
      strand: PropTypes.string,
      total_coding_length: PropTypes.number,
      total_exon_length: PropTypes.number,
    })
  ),
  genome: PropTypes.shape({
    default: PropTypes.bool,
    description: PropTypes.string,
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  SetCurrentVariant: PropTypes.func.isRequired,
  varsomeHost: PropTypes.string,
  cardsUIEnabled: PropTypes.bool,
};

TranscriptDetails.defaultProps = {
  transcripts: [],
  genome: [],
  varsomeHost: null,
  cardsUIEnabled: false,
};

const mapStateToProps = (state) => ({
  transcripts: getTranscripts(state),
  genome: getGenome(state),
  varsomeHost: getVarsomeHost(state),
  cardsUIEnabled: getCardsUIEnabled(state),
});

const mapDispatchToProps = (dispatch) => {
  return {
    SetCurrentVariant: (params) => dispatch(setCurrentVariant(params)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TranscriptDetails);
